import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Building2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PickerSearch, matchesQuery } from "@/components/admin/picker-search";
import { CompanySheet } from "@/components/subs/company-sheet";
import { SubWorkerProfile } from "@/components/subs/sub-worker-profile";
import { apiGet, apiPatch } from "@/lib/api";
import { cn } from "@/lib/utils";
import { SearchableSelect } from "@/components/ui/searchable-select";

interface SubProject {
  id: string;
  name: string;
  subCount: number;
}

interface SubCompliance {
  userId: string;
  name: string;
  email: string;
  companyName: string | null;
  trade: string | null;
  totalFlhas: number;
  lastFlhaDate: string | null;
  unsignedFlhas: number;
}

export const Route = createFileRoute(
  "/_authenticated/subcontractors/"
)({
  validateSearch: (search: Record<string, unknown>): { tab?: "requests" } => ({
    tab: search.tab === "requests" ? "requests" : undefined,
  }),
  component: SubcontractorsPage,
});

function ComplianceTab() {
  const [projectId, setProjectId] = useState<string>("");

  const { data: projectsData } = useQuery({
    queryKey: ["sub-projects"],
    queryFn: () =>
      apiGet<{ data: SubProject[] }>("/api/subcontractors/projects"),
  });

  const projects = projectsData?.data ?? [];

  const { data: complianceData, isLoading } = useQuery({
    queryKey: ["sub-compliance", projectId],
    queryFn: () =>
      apiGet<{ data: SubCompliance[] }>(
        `/api/subcontractors/compliance?projectId=${projectId}`
      ),
    enabled: !!projectId,
  });

  const subs = complianceData?.data ?? [];

  if (projects.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>No subcontractors have been onboarded yet.</p>
        <p className="text-sm mt-1">
          Generate an invite link from a project to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="w-full md:w-72">
        <SearchableSelect
          value={projectId}
          onChange={setProjectId}
          allLabel="Select a project"
          allValue=""
          options={projects.map((p) => ({ value: p.id, label: p.name, hint: `(${p.subCount} subs)` }))}
        />
      </div>

      {!projectId && (
        <p className="text-sm text-muted-foreground">
          Select a project to view subcontractor compliance.
        </p>
      )}

      {projectId && isLoading && (
        <div className="text-center py-8">
          <div className="w-6 h-6 border-2 border-paris-red border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      )}

      {projectId && !isLoading && subs.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          No subcontractors have been invited to this project yet.
        </div>
      )}

      {projectId && subs.length > 0 && (
        <div className="space-y-2">
          {subs.map((sub) => {
            const daysSinceLast = sub.lastFlhaDate
              ? Math.floor(
                  (Date.now() - new Date(sub.lastFlhaDate).getTime()) /
                    86400000
                )
              : null;

            let statusBadge: { label: string; className: string };
            if (sub.totalFlhas === 0) {
              statusBadge = {
                label: "No FLHAs",
                className: "bg-amber-100 text-amber-700",
              };
            } else if (sub.unsignedFlhas > 0) {
              statusBadge = {
                label: `${sub.unsignedFlhas} unsigned`,
                className: "bg-red-100 text-red-700",
              };
            } else if (daysSinceLast !== null && daysSinceLast > 7) {
              statusBadge = {
                label: "Inactive",
                className: "bg-yellow-100 text-yellow-700",
              };
            } else {
              statusBadge = {
                label: "Active",
                className: "bg-green-100 text-green-700",
              };
            }

            return (
              <div
                key={sub.userId}
                className="border rounded-lg p-4 bg-white flex flex-col md:flex-row md:items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm truncate">{sub.name}</p>
                    {sub.companyName && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full shrink-0">
                        {sub.companyName}
                      </span>
                    )}
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${statusBadge.className}`}
                    >
                      {statusBadge.label}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {sub.trade || "No trade"} · {sub.email}
                  </p>
                </div>
                <div className="flex gap-6 text-xs text-muted-foreground shrink-0">
                  <div className="text-center">
                    <p className="font-semibold text-sm text-text-primary">
                      {sub.totalFlhas}
                    </p>
                    <p>FLHAs</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-sm text-text-primary">
                      {sub.lastFlhaDate
                        ? new Date(sub.lastFlhaDate).toLocaleDateString(
                            "en-CA",
                            { month: "short", day: "numeric" }
                          )
                        : "Never"}
                    </p>
                    <p>Last FLHA</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-sm text-text-primary">
                      {sub.unsignedFlhas}
                    </p>
                    <p>Unsigned</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


interface CompanyRow {
  id: string;
  name: string;
  contactName: string | null;
  contactPhone: string | null;
  status: string;
  approvedWorkers: number;
  pendingWorkers: number;
  activeProjects: number;
}

interface RequestRow {
  membershipId: string;
  company: { id: string; name: string };
  user: { id: string; name: string; email: string; phone: string | null; trade: string | null };
  isNewAccount: boolean;
  otherCompanies: string[];
  projects: { projectName: string; divisionName: string }[];
}

function SubcontractorsPage() {
  const { user } = Route.useRouteContext();
  const isManager = user.role === "admin" || user.role === "div_admin";
  const { tab: tabFromLink } = Route.useSearch();
  const [tab, setTab] = useState<"companies" | "requests" | "compliance">(isManager && tabFromLink === "requests" ? "requests" : "companies");

  const { data: reqData } = useQuery({
    queryKey: ["sub-requests"],
    queryFn: () => apiGet<{ data: RequestRow[] }>("/api/subs/approvals"),
    enabled: isManager,
    refetchInterval: 60_000,
  });
  const pendingCount = reqData?.data.length ?? 0;

  const tabBtn = (active: boolean) =>
    cn(
      "px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
      active ? "bg-surface-raised text-text-primary shadow-sm" : "text-muted-foreground hover:text-text-primary"
    );

  return (
    <div className="space-y-4">
      <div className="flex gap-1 rounded-lg border border-border p-1 bg-muted/30 w-fit">
        <button type="button" className={tabBtn(tab === "companies")} onClick={() => setTab("companies")}>Companies</button>
        {isManager && (
          <button type="button" className={tabBtn(tab === "requests")} onClick={() => setTab("requests")}>
            Sign-up requests{pendingCount > 0 ? ` · ${pendingCount}` : ""}
          </button>
        )}
        <button type="button" className={tabBtn(tab === "compliance")} onClick={() => setTab("compliance")}>Compliance</button>
      </div>

      {tab === "companies" && <CompaniesTab isManager={isManager} />}
      {tab === "requests" && isManager && <RequestsTab rows={reqData?.data ?? []} />}
      {tab === "compliance" && <ComplianceTab />}
    </div>
  );
}

function CompaniesTab({ isManager }: { isManager: boolean }) {
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["sub-companies"],
    queryFn: () => apiGet<{ data: CompanyRow[] }>("/api/subs/companies"),
  });
  const rows = (data?.data ?? []).filter((c) => matchesQuery(query, c.name, c.contactName));

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex-1"><PickerSearch value={query} onChange={setQuery} placeholder="Search companies..." /></div>
        {isManager && (
          <Button onClick={() => { setOpenId(null); setSheetOpen(true); }} className="h-8 gap-1">
            <Plus className="h-4 w-4" /> Add company
          </Button>
        )}
      </div>

      {isLoading && <p className="py-6 text-center text-sm text-muted-foreground">Loading...</p>}
      {!isLoading && rows.length === 0 && (
        <div className="py-12 text-center text-muted-foreground">
          <Building2 className="mx-auto mb-2 h-8 w-8 opacity-40" />
          <p className="text-sm">{isManager ? "No subcontractor companies yet." : "No subcontractors on your projects yet."}</p>
        </div>
      )}
      <div className="space-y-2">
        {rows.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => { setOpenId(c.id); setSheetOpen(true); }}
            className="flex w-full flex-wrap items-center gap-3 rounded-lg border bg-card p-3 text-left hover:bg-muted/30"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{c.name}{c.status === "inactive" ? " (inactive)" : ""}</p>
              <p className="text-xs text-muted-foreground">{[c.contactName, c.contactPhone].filter(Boolean).join(" · ") || "No contact yet"}</p>
            </div>
            <div className="flex gap-4 text-center text-xs text-muted-foreground">
              <div><p className="text-sm font-semibold text-foreground">{c.activeProjects}</p><p>Projects</p></div>
              <div><p className="text-sm font-semibold text-foreground">{c.approvedWorkers}</p><p>People</p></div>
              {c.pendingWorkers > 0 && <div><p className="text-sm font-semibold text-amber-600">{c.pendingWorkers}</p><p>Waiting</p></div>}
            </div>
          </button>
        ))}
      </div>

      <CompanySheet companyId={openId} open={sheetOpen} onOpenChange={setSheetOpen} />
    </div>
  );
}

function RequestsTab({ rows }: { rows: RequestRow[] }) {
  const qc = useQueryClient();
  const [profileId, setProfileId] = useState<string | null>(null);
  const decide = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "approve" | "reject" }) => apiPatch(`/api/subs/workers/${id}/${action}`),
    onSuccess: (_d, v) => {
      toast.success(v.action === "approve" ? "Approved. They have been told." : "Request rejected");
      qc.invalidateQueries({ queryKey: ["sub-requests"] });
      qc.invalidateQueries({ queryKey: ["sub-companies"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (rows.length === 0) {
    return <p className="py-12 text-center text-sm text-muted-foreground">No sign-up requests waiting.</p>;
  }
  return (
    <div className="space-y-2">
      <SubWorkerProfile userId={profileId} onClose={() => setProfileId(null)} />
      {rows.map((r) => (
        <div key={r.membershipId} className="flex flex-wrap items-center gap-3 rounded-lg border bg-card p-3">
          <div className="min-w-0 flex-1 space-y-0.5">
            <p className="text-sm font-semibold">
              <button type="button" className="text-left hover:underline underline-offset-2" onClick={() => setProfileId(r.user.id)} title="Open profile">{r.user.name}</button>{" "}
              <span className="font-normal text-muted-foreground">for {r.company.name}</span>
            </p>
            <p className="text-xs text-muted-foreground">{[r.user.trade, r.user.phone, r.user.email].filter(Boolean).join(" · ")}</p>
            <p className="text-xs text-muted-foreground">
              {r.projects.map((p) => `${p.projectName} (${p.divisionName})`).join(", ")}
            </p>
            <p className="text-[11px] text-amber-600">
              {r.isNewAccount ? "New account" : `Already with ${r.otherCompanies.length ? r.otherCompanies.join(", ") : "another company"}: new company link`}
            </p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" disabled={decide.isPending} onClick={() => decide.mutate({ id: r.membershipId, action: "approve" })}>Approve</Button>
            <Button size="sm" variant="outline" className="text-destructive" disabled={decide.isPending}
              onClick={() => decide.mutate({ id: r.membershipId, action: "reject" })}>Reject</Button>
          </div>
        </div>
      ))}
    </div>
  );
}
